import { supabase } from "./supabase";

const baseUrl = import.meta.env.VITE_BE_URL;

export const api = async <T>(
  path: string,
  body: unknown = {},
): Promise<T> => {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error("Not authenticated");

  const res = await fetch(`${baseUrl}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error ?? "Request failed");
  }

  return res.json() as Promise<T>;
};

/**
 * Releases a room membership using an explicitly supplied token.
 *
 * Needed by sign-in: the guest's membership has to be given up *after* the new
 * credentials are proven, by which point the session has already been replaced
 * and `api()` would authenticate as the wrong person. The guest's own token is
 * still valid — signing in elsewhere does not revoke it — so it is captured
 * beforehand and used here.
 *
 * Unlike the socket command this is awaitable and answered, so the caller
 * learns whether the membership actually went away. A 404 means the server no
 * longer has them in a room, which is the outcome being asked for.
 */
export const leaveRoomAs = async (token: string): Promise<boolean> => {
  try {
    const res = await fetch(`${baseUrl}/room/room-leave`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: "{}",
    });
    return res.ok || res.status === 404;
  } catch {
    return false;
  }
};
